import React, {useCallback, useRef, useState} from "react";

import ShapeSparkline from "./ShapeSparkline";

// Bounds the getImageData pixel count (and SHAPE_MAX_POINTS=2000 on the backend, which this
// stays well under even before trimming empty margins) — a typical chart screenshot easily
// exceeds this, so the image is downscaled onto an offscreen canvas before scanning.
const MAX_WIDTH = 1000;
const MAX_HEIGHT = 800;
// A pixel within this RGB distance of the sampled background is treated as background, not line.
const BG_DISTANCE_THRESHOLD = 30;
// max(r,g,b) - min(r,g,b) below this reads as gray/black — gridlines, axes, text, candle
// wicks — not a colored price line. Filters those out of the line-color histogram.
const GRAYSCALE_CHROMA_THRESHOLD = 18;
// A pixel within this RGB distance of the detected line color counts as "on the line" when
// scanning each column.
const LINE_COLOR_DISTANCE_THRESHOLD = 40;
const QUANTIZE_STEP = 16;
// Below this many columns with a confident line-color match, the image isn't a simple
// single-line chart we can digitize — bail out with an error rather than submit noise.
const MIN_MATCHED_COLUMNS = 20;
// How many candidate colors to trace before giving up. Real chart screenshots need far more
// than a handful: anti-aliasing and gradient fills fragment one visual element across dozens
// of quantized buckets, and the pale ones (a gradient's washed-out end, a reference line's
// blend with the background) cover MORE columns than the price line itself. Measured on real
// uploads, the true price data ranked 6th-9th by coverage — a cap of 5 never reached it.
const MAX_CANDIDATE_COLORS = 12;
// Robust spread bar: the INTERQUARTILE range of a candidate's traced y-values, as a fraction
// of image height. Range (max-min) was tried first and is unusable here — it is dominated by
// outliers, so a dead-flat gradient band with a couple of stray pixels scored 99% of height
// and sailed through. Measured across real uploads the separation is wide and clean:
// artifacts (gradient bands, reference lines, anti-aliasing halos) land at 0-13% IQR, real
// price data at 20-43%. 15% sits in the gap with margin on both sides.
const MIN_LINE_IQR_FRACTION = 0.15;

interface Props {
  /** Same contract as ShapeCanvas's onChange: values normalized 0-100 (0 = bottom, 100 = top);
   * the backend z-normalizes so absolute scale never matters. Called once per successfully
   * digitized image, and with [] on Clear / when digitization fails. */
  onChange: (shape: number[]) => void;
}

export interface ShapeImageUploadHandle {
  clear: () => void;
}

type RGB = {r: number; g: number; b: number};

function colorDistance(a: RGB, b: RGB): number {
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

function isGrayscale(c: RGB): boolean {
  return Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b) < GRAYSCALE_CHROMA_THRESHOLD;
}

function quantize(c: RGB): RGB {
  const q = (v: number) => Math.round(v / QUANTIZE_STEP) * QUANTIZE_STEP;
  return {r: q(c.r), g: q(c.g), b: q(c.b)};
}

function interpolateGaps(arr: (number | null)[]): number[] {
  // Callers only pass an array already trimmed of leading/trailing nulls, so every
  // remaining gap here is interior and both neighbors are guaranteed non-null.
  const result = arr.slice();
  let i = 0;
  while (i < result.length) {
    if (result[i] !== null) { i++; continue; }
    let j = i;
    while (j < result.length && result[j] === null) j++;
    const prev = result[i - 1] as number;
    const next = result[j] as number;
    for (let k = i; k < j; k++) {
      const t = (k - i + 1) / (j - i + 1);
      result[k] = prev + (next - prev) * t;
    }
    i = j;
  }
  return result as number[];
}

/** Column index -> pixel y of the detected line, or null where no confident match was found.
 * Exported for the preview overlay to reuse the same detection pass it drew from. */
export function detectLineColumns(imageData: ImageData): {lineColumns: (number | null)[]; matchedCount: number} {
  const {width, height, data} = imageData;
  const sample = (x: number, y: number): RGB => {
    const i = (y * width + x) * 4;
    return {r: data[i], g: data[i + 1], b: data[i + 2]};
  };

  // Chart screenshot corners are virtually always uniform background (plot area or page chrome).
  const bg = sample(0, 0);

  // Tracked per bucket: which COLUMNS a color touches, not just how many pixels — see the
  // ranking comment below for why coverage, not raw pixel count, is the right criterion.
  const counts = new Map<string, {color: RGB; columns: Set<number>}>();
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 200) continue; // mostly-transparent
      const c: RGB = {r: data[i], g: data[i + 1], b: data[i + 2]};
      if (colorDistance(c, bg) < BG_DISTANCE_THRESHOLD) continue;
      if (isGrayscale(c)) continue;
      const q = quantize(c);
      const key = `${q.r},${q.g},${q.b}`;
      const existing = counts.get(key);
      if (existing) existing.columns.add(x);
      else counts.set(key, {color: q, columns: new Set([x])});
    }
  }

  const candidates = [...counts.values()]
    .sort((a, b) => b.columns.size - a.columns.size)
    .slice(0, MAX_CANDIDATE_COLORS);

  // Trace every candidate and pick the one whose trace actually looks like a price line,
  // rather than accepting the first that clears a bar. On real screenshots, coverage order
  // puts washed-out artifacts first and spread ranks them above the real line too, so the
  // choice is made on the trace's own smoothness (see the jitter comment below).
  let best: {lineColumns: (number | null)[]; matchedCount: number; jitter: number} | null = null;
  for (const {color} of candidates) {
    const columns: (number | null)[] = new Array(width).fill(null);
    const tops: number[] = [];
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const c = sample(x, y);
        // Same two exclusions the histogram applies. Without them a barely-chromatic
        // candidate (a pale anti-aliasing halo) matches every gray thing within the color
        // distance — gridlines, volume bars, axis text — and traces their envelope. That
        // was the real cause of a candlestick chart digitizing as dense noise: the winning
        // color held 469 pixels of its own but matched 961 columns of borrowed gray.
        if (colorDistance(c, bg) < BG_DISTANCE_THRESHOLD || isGrayscale(c)) continue;
        if (colorDistance(c, color) < LINE_COLOR_DISTANCE_THRESHOLD) {
          // Topmost match, not the median: a filled area chart's color match spans from the
          // stroke down through the fill to the axis, and the real price is the TOP of that
          // span. A bare stroke is a couple px thick either way, so this is equivalent
          // there. Breaking on the first hit also makes the extra candidates affordable.
          columns[x] = y;
          tops.push(y);
          break;
        }
      }
    }
    if (tops.length < MIN_MATCHED_COLUMNS) continue;
    const sorted = [...tops].sort((a, b) => a - b);
    const at = (p: number): number => sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))];
    if (at(0.75) - at(0.25) < height * MIN_LINE_IQR_FRACTION) continue; // flat: annotation, not data

    // Median vertical step between adjacent traced columns. A rendered price line moves a
    // pixel or two per column; a washed-out blend picks up scattered unrelated content and
    // lurches. This is the criterion that survives across real charts — neither coverage nor
    // spread does. A pale green wash beat the actual line on both (more columns covered,
    // and a WIDER spread, because scatter spans more of the image than the line does) while
    // tracing at 7px/column against the real line's 1px.
    const steps: number[] = [];
    for (let i = 1; i < tops.length; i++) steps.push(Math.abs(tops[i] - tops[i - 1]));
    steps.sort((a, b) => a - b);
    const jitter = steps.length ? steps[Math.floor(steps.length / 2)] : 0;

    // Smoothest wins; among equally smooth candidates, the one covering more columns.
    if (!best || jitter < best.jitter || (jitter === best.jitter && tops.length > best.matchedCount)) {
      best = {lineColumns: columns, matchedCount: tops.length, jitter};
    }
  }
  if (!best) return {lineColumns: [], matchedCount: 0};
  return {lineColumns: best.lineColumns, matchedCount: best.matchedCount};
}

// Extracts a price-line shape from chart image pixel data. Works well for a simple single-
// color line/area chart screenshot; NOT for candlestick/OHLC or busy multi-series charts —
// those need real bar/wick geometry parsing, a separate, harder CV problem (see PR.8 in
// agent_docs/roadmap.md). Returns [] when no confident line was found.
export function digitize(imageData: ImageData): number[] {
  const {lineColumns, matchedCount} = detectLineColumns(imageData);
  if (matchedCount < MIN_MATCHED_COLUMNS) return [];

  const firstIdx = lineColumns.findIndex((v) => v !== null);
  const lastIdx = lineColumns.length - 1 - [...lineColumns].reverse().findIndex((v) => v !== null);
  const trimmed = lineColumns.slice(firstIdx, lastIdx + 1);
  const filled = interpolateGaps(trimmed);

  return filled.map((y) => (1 - y / imageData.height) * 100);
}

// Static read-only line plot of the extracted shape, right next to the source image, so a
// user can immediately sanity-check the heuristic actually traced the intended line before
// spending a query on it — this digitization is a best-effort heuristic (see the digitize()
// comment above) and can silently pick up the wrong line on a busy chart.
const ShapeImageUpload = React.forwardRef<ShapeImageUploadHandle, Props>(function ShapeImageUpload(
  {onChange},
  ref,
) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractedShape, setExtractedShape] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setExtractedShape([]);
    setError(null);
    onChange([]);
  }, [onChange]);

  React.useImperativeHandle(ref, () => ({clear: reset}), [reset]);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("That doesn't look like an image file.");
      return;
    }
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_WIDTH / img.width, MAX_HEIGHT / img.height);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setError("Couldn't read this image.");
        URL.revokeObjectURL(url);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const shape = digitize(imageData);
      if (shape.length < 2) {
        setError(
          "Couldn't find a clear, single-color price line in this image. This works best on a " +
          "simple line or area chart screenshot — not candlestick charts or busy multi-line charts.",
        );
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setExtractedShape([]);
        onChange([]);
        return;
      }
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setExtractedShape(shape);
      onChange(shape);
    };
    img.onerror = () => {
      setError("Couldn't load this image.");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [onChange]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div
      className={`shape-upload-dropzone${dragOver ? " drag-over" : ""}`}
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="shape-upload-input"
        onChange={onInputChange}
      />
      {previewUrl ? (
        <div className="shape-upload-result">
          <img src={previewUrl} alt="Uploaded chart" className="shape-upload-preview" />
          {extractedShape.length > 0 && (
            <div className="shape-upload-extracted">
              <div className="shape-upload-extracted-label">What we&apos;ll match against:</div>
              <ShapeSparkline shape={extractedShape} />
            </div>
          )}
        </div>
      ) : (
        <div className="shape-upload-placeholder">
          <p>Click or drag a chart image here</p>
          <p className="shape-upload-hint">Works best on a simple line or area chart screenshot</p>
        </div>
      )}
      {error && <div className="shape-upload-error">{error}</div>}
    </div>
  );
});

export default ShapeImageUpload;
